/**
 * Pin visibility for Path A (extends S8 gmOnly).
 *
 * Modes (Atlas / plugin metadata on each pin):
 *  - "gm"      — Just me (GM only). Same as gmOnly: true.
 *  - "players" — All players in the session (+ GM).
 *  - "player"  — One seated player whose displayName matches visibleTo.
 *
 * Legacy: gmOnly true → gm; otherwise → players.
 */

export type PinVisibilityMode = "gm" | "players" | "player";

export type PinVisibilityFields = {
  visibility: PinVisibilityMode;
  /** Display-name key when visibility === "player". */
  visibleTo: string | null;
  /** Derived: true iff visibility === "gm" (keeps S8 filters working). */
  gmOnly: boolean;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Map a raw visibility string to a canonical mode.
 * Returns null when the field is absent; "unknown" when present but unrecognized
 * (callers must fail closed — never treat unknown as public).
 */
export function canonicalizePinVisibility(
  raw: unknown,
): PinVisibilityMode | "unknown" | null {
  if (raw === undefined || raw === null || raw === "") return null;
  if (typeof raw !== "string") return "unknown";
  const key = raw.trim().toLowerCase();
  if (
    key === "gm" ||
    key === "gm-only" ||
    key === "gm_only" ||
    key === "just-me" ||
    key === "just_me" ||
    key === "private" ||
    key === "hidden"
  ) {
    return "gm";
  }
  if (
    key === "players" ||
    key === "public" ||
    key === "all" ||
    key === "everyone" ||
    key === "shared"
  ) {
    return "players";
  }
  if (key === "player") {
    return "player";
  }
  // e.g. "specific", typos, foreign enums — fail closed upstream
  return "unknown";
}

export function resolvePinVisibility(raw: unknown): PinVisibilityFields {
  const pin = isRecord(raw) ? raw : {};

  const visRaw = pin.visibility ?? pin.shareMode ?? pin.access;
  const canonical = canonicalizePinVisibility(visRaw);

  let visibleTo: string | null = null;
  const visibleRaw =
    pin.visibleTo ?? pin.visibleToPlayer ?? pin.visibleToName ?? null;
  if (typeof visibleRaw === "string" && visibleRaw.trim()) {
    visibleTo = visibleRaw.trim().slice(0, 64);
  }

  let visibility: PinVisibilityMode;
  if (canonical === "unknown") {
    // Present but unrecognized → GM-only (never open to the room).
    visibility = "gm";
  } else if (canonical === null) {
    // Missing visibility → legacy gmOnly / players default.
    // Atlas note pins often default gmOnly:true; Path A "Public" stamps
    // visibility:"players" + gmOnly:false. Prefer explicit false over missing.
    if (pin.gmOnly === true) {
      visibility = "gm";
    } else if (
      pin.gmOnly === false ||
      pin.shared === true ||
      pin.playerVisible === true
    ) {
      visibility = "players";
    } else {
      visibility = "players";
    }
  } else {
    visibility = canonical;
  }

  if (visibility === "player" && !visibleTo) {
    // Incomplete specific-player pin → safer to treat as GM-only.
    visibility = "gm";
  }

  return {
    visibility,
    visibleTo: visibility === "player" ? visibleTo : null,
    gmOnly: visibility === "gm",
  };
}

export function pinVisibleToViewer(
  pin: PinVisibilityFields,
  viewer: { role: "gm" | "player"; displayName: string },
): boolean {
  if (viewer.role === "gm") return true;
  switch (pin.visibility) {
    case "gm":
      return false;
    case "players":
      return true;
    case "player": {
      const want = (pin.visibleTo ?? "").trim().toLowerCase();
      const have = viewer.displayName.trim().toLowerCase();
      return want.length > 0 && want === have;
    }
    default: {
      const _exhaustive: never = pin.visibility;
      void _exhaustive;
      return false;
    }
  }
}

/** Paths whose sheet text must stay off viewers who cannot see any pin using them. */
export function gmRestrictedNotePaths(
  pins: Array<PinVisibilityFields & { notePath: string | null }>,
): Set<string> {
  const paths = new Set<string>();
  for (const pin of pins) {
    if (pin.gmOnly && pin.notePath) paths.add(pin.notePath);
  }
  return paths;
}
